import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Secure token storage using sessionStorage (shorter-lived, cleared on tab close)
// Combined with httpOnly cookies for server-side validation
const secureTokenStorage = {
  getToken: () => {
    try {
      return sessionStorage.getItem('auth_token');
    } catch {
      return null;
    }
  },
  setToken: (token) => {
    try {
      if (token) {
        sessionStorage.setItem('auth_token', token);
      } else {
        sessionStorage.removeItem('auth_token');
      }
    } catch {
      // Silent fail for SSR or storage unavailable
    }
  },
  removeToken: () => {
    try {
      sessionStorage.removeItem('auth_token');
      // Also clear localStorage for migration
      localStorage.removeItem('token');
    } catch {
      // Silent fail
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    // Migrate from localStorage to sessionStorage if needed
    const sessionToken = secureTokenStorage.getToken();
    if (sessionToken) return sessionToken;
    
    const legacyToken = localStorage.getItem('token');
    if (legacyToken) {
      secureTokenStorage.setToken(legacyToken);
      localStorage.removeItem('token');
      return legacyToken;
    }
    return null;
  });

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = secureTokenStorage.getToken();
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch {
        secureTokenStorage.removeToken();
        setToken(null);
        setUser(null);
      }
    } else {
      // Try cookie-based auth (httpOnly cookies set by server)
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch {
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, user: userData } = response.data;
    secureTokenStorage.setToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, isBusinessOwner = false) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      is_business_owner: isBusinessOwner
    }, { withCredentials: true });
    const { token: newToken, user: userData } = response.data;
    secureTokenStorage.setToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    secureTokenStorage.removeToken();
    setToken(null);
    setUser(null);
  };

  const becomeOwner = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.put(`${API}/auth/become-owner`, {}, { headers, withCredentials: true });
    setUser(prev => ({ ...prev, is_business_owner: true }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      loginWithGoogle,
      handleOAuthCallback,
      logout,
      becomeOwner,
      isAuthenticated: !!user,
      isBusinessOwner: user?.is_business_owner || false
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
